import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAllBooksInfinite } from '../hooks/useAllBooks';
import BookCard from '../components/BookCard';
import BookCardList from '../components/BookCardList';
import ViewControls from '../components/ViewControls';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

const CategoryBooksPage: React.FC = () => {
  const navigate = useNavigate();
  const { categoryName = '' } = useParams<{ categoryName: string }>();
  const decodedCategory = decodeURIComponent(categoryName);
  const normalizeCategory = (s: string) => {
    const withSpaces = s.replace(/\u200c/g, ' ');
    return withSpaces.replace(/(\S)(شناسی)/g, '$1 شناسی');
  };
  
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'newest' | 'popular'>('newest');

  const sortParam = sortBy === 'newest' ? 'newest' : 'rating';
  
  const {
    books,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useAllBooksInfinite(decodedCategory, { limit: 10, sortBy: sortParam as 'newest' | 'rating' });

  const { loadMoreRef } = useInfiniteScroll({
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    onLoadMore: fetchNextPage,
  });

  const carouselBooks = useMemo(() => books.map((b) => ({
    book_id: b.book_id,
    title: b.title,
    author: b.author,
    cover_image_url: b.cover_image_url ?? null,
    topic: b.topic,
  })), [books]);

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      <div className="px-4 py-3 bg-white border-b border-gray-100 flex items-center justify-between sticky top-0 z-50 pt-safe-top">
        <div className="flex items-center justify-between w-full mt-2">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-all duration-200 hover:scale-110"
          aria-label="بازگشت"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>
        <h1 className="text-sm md:text-base font-bold text-foreground font-persian">{normalizeCategory(decodedCategory)}</h1>
        <div className="w-8"></div>
        </div>
      </div>

      <div className="px-4 pb-8">
        <ViewControls
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          sortBy={sortBy}
          onSortChange={setSortBy}
          className="mb-4 pt-4"
        />

        {isLoading && (
          <div className="text-center py-8 text-muted-foreground font-persian">در حال بارگذاری...</div>
        )}
        {error && (
          <div className="text-center py-8 text-red-500 font-persian">خطا در دریافت کتاب‌های دسته</div>
        )}
        {!isLoading && !error && books.length === 0 && (
          <div className="text-center py-8 text-muted-foreground font-persian">کتابی یافت نشد</div>
        )}
        {books.length > 0 && (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-3 gap-y-6">
                {books.map((b, index) => (
                  <BookCard
                    key={b.book_id}
                    book={b}
                    onClick={() =>
                      navigate(`/library/book`, {
                        state: {
                          bookId: b.book_id,
                          source: 'category',
                          categoryName: decodedCategory,
                          books: carouselBooks,
                          initialIndex: index,
                        },
                      })
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {books.map((b, index) => (
                  <BookCardList
                    key={b.book_id}
                    book={b}
                    className="bg-transparent shadow-none"
                    onClick={() =>
                      navigate(`/library/book`, {
                        state: {
                          bookId: b.book_id,
                          source: 'category',
                          categoryName: decodedCategory,
                          books: carouselBooks,
                          initialIndex: index,
                        },
                      })
                    }
                  />
                ))}
              </div>
            )}

            {/* Infinite scroll trigger */}
            <div ref={loadMoreRef} className="h-4" />
            
            {isFetchingNextPage && (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryBooksPage;
